import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useHistory } from 'react-router-dom';
import { Typography } from 'seagull';
import { useEventReporter, useGlobalContext, useStoreActions, useStoreStatic } from "../../contexts";
import { returnToStorageKey, routes } from "../../constants";
import { useOnMount } from "../../hooks/useOnMount";
import { AnchorLogo } from 'seagull/icons';
import { notificationRouter } from "./notificationRouter";
import styles from "./Notification.module.scss";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
const acceptNotificationLoader = 'acceptNotification';
const invalidInvitationError = 'invalidInvitation';
function InvalidInvitationIndicator() {
  return /*#__PURE__*/React.createElement("div", {
    className: styles.invalidWrapper,
    "data-testid": "invalid-invitation"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AnchorLogo, {
    className: styles.logo
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    weight: "normal"
  }, "This invitation link is no longer valid"), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, "If you would like to join the account, we recommend contacting the person who invited you."), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, "For support, please email support@sayanchor.com.")));
}
export default function Notification(props) {
  const {
    notificationId
  } = props.match.params;
  const [error, setError] = useState();
  const {
    reportToHeap,
    reportToGtm,
    reportToAppcues,
    reportToHubspot
  } = useEventReporter();
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    setAuthToken
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const location = useLocation();
  const history = useHistory();
  useOnMount(() => {
    async function handleAuthenticationAndRedirect() {
      const isLoggedIn = !!getState().authToken;
      globalContext.startLoader(acceptNotificationLoader);
      let redirectToUrl, stateChanges;
      try {
        const notification = await (isLoggedIn ? api.notification.getWhileLoggedIn(notificationId) : api.notification.getWhileLoggedOut(notificationId));
        if (notification.authToken != null) {
          await setAuthToken(notification.authToken);
        }
        if (notification && notification.notificationType) {
          reportToGtm('notification-open', notification.notificationType);
          reportToAppcues('notification-open', notification.notificationType);
          reportToHeap('notification', 'open', notification.notificationType);
        }
        const {
          redirect,
          error,
          hubspotEventName
        } = notificationRouter(notification);
        hubspotEventName && reportToHubspot(hubspotEventName);
        redirectToUrl = redirect.url;
        stateChanges = redirect.state;
        if (error) {
          const err = new Error(error + " - type:".concat(notification.type || '(empty)', ";notificationType:").concat(notification.notificationType || '(empty)'));
          Sentry.withScope(scope => {
            scope.setContext('Notification data', {
              notificationPayload: JSON.stringify(notification)
            });
            Sentry.captureException(err);
          });
          globalContext.showErrorToast("Oops! We couldn't find the correct page. Please contact support@sayanchor.com and let us know.", 'notification-redirect-toast');
        }
      } catch (err) {
        const e = new ErrorResponse(err);
        if (e.status === 401) {
          if (isLoggedIn) {
            setAuthToken(null);
          }
          window.localStorage.setItem(returnToStorageKey, location.pathname);
          redirectToUrl = routes.redirect;
        } else if (e.hasServerError(ServerError.RecipientNotFound) && isLoggedIn) {
          // logout flow will trigger an unmount/mount cycle because this component renders only when authFlowDone is true.
          await setAuthToken(null);
        } else if (e.hasServerError(ServerError.InvalidTeamInvitation)) {
          setError(invalidInvitationError);
        } else {
          redirectToUrl = routes.home;
        }
      }
      if (redirectToUrl) {
        history.replace(redirectToUrl, stateChanges);
      }
      globalContext.stopLoader(acceptNotificationLoader);
    }
    handleAuthenticationAndRedirect();
  });
  return error === invalidInvitationError ? /*#__PURE__*/React.createElement(InvalidInvitationIndicator, null) : null;
}